<template>
    <div class="segment-wrapper"
         id="segment-container"
         ref="translationSegment">
        
        <div v-loading="segmentItem.loading"
             :class="[{'focused-row': segmentItem.focused}, 'segment-box']">

            <StyleBadge :stylesApplied="translationSegment.stylesApplied"
                        :styleName="translationSegment.paragraphStyleName" />

            <div class="number" id="row-number">
                {{ segmentItem.rowNumber }}
            </div>

            <div class="text-group">
                <div class="source-target">
                    <div class="source-text"
                         id="source-container">
                        <SourceSegment v-model="translationSegment.sourceData" />
                    </div>
                    <TargetEditorClearable v-model="segmentItem"
                                          :disableFormatting="isIdmlDocument" />

                </div>

                <TranslationVariantsPopup v-model="segmentItem" />
            </div>

            <SegmentControls v-model="segmentItem" />

            <div class="next-segment-wrapper">
                <FindNextSegment :focused="segmentItem.focused"
                                 :translationLoadded="isTranslationLoaded"
                                 @next-translate-segment="handleNextSegment" />
            </div>
        </div>
        
        <MergeButton :class="[{ show: showMergeButton }, 'hidden']"
                     :focused="segmentItem.focused"
                     :disabled="mergeDisabled"
                     :documentType="currentDocumentType"
                     @merge="handleMerge"
                     />
        <SplitButton v-if="splitPossible"
                     :focused="segmentItem.focused"
                     @split="handleSplit"
                     />
    </div>
</template>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';
    import { translationSegmentService } from './translationSegmentService';
    import { SegmentEditorItem } from '@/models/SegmentEditorItem';
    import { TranslationSegment } from '@/models/TranslationSegment';
    import { documentStore } from '@/stores/documentStore';

    import SourceSegment from './SourceSegmentView.vue';
    import TargetEditorClearable from './TargetEditorClearable.vue';
    import SegmentControls from './SegmentControls.vue';
    import FindNextSegment from './FindNextSegment.vue';
    import MergeButton from './MergeButton.vue';
    import SplitButton from './SplitButton.vue';
    import StyleBadge from './StyleBadge.vue';

    import TranslationVariantsPopup from './TranslationVariantsPopup.vue'


    export default defineComponent({
        name: 'TranslationSegmentEditor',
        components: {
            SourceSegment,
            TargetEditorClearable,
            SegmentControls,
            FindNextSegment,
            MergeButton,
            SplitButton,
            TranslationVariantsPopup,
            StyleBadge,
        },
        props: {
            modelValue: {
                type: Object as PropType<SegmentEditorItem>,
                required: true
            },
        },
        emits: [
            'update:modelValue',
            'merge-segment',
            'split-segment',
            'next-translate-segment',
        ],
        data() {
            return {
                translationSegmentService: translationSegmentService.getInstance(),
                documentStore: documentStore(),
            };
        },
        computed: {
            segmentItem: {
                get(): SegmentEditorItem {
                    return this.modelValue as SegmentEditorItem;
                },
                set(value: SegmentEditorItem) {
                    this.$emit('update:modelValue', value);
                }
            },
            translationSegment(): TranslationSegment {
                return this.segmentItem.data as TranslationSegment;
            },
            isTranslationLoaded(): boolean {
                return !!this.translationSegment.translationVariants;
            },
            showMergeButton(): boolean {
                return this.translationSegmentService.showMergeButton();
            },
            mergeDisabled(): boolean {
                return this.translationSegmentService.isMergeDisabled(this.segmentItem);
            },
            mergePossible(): boolean {
                return this.translationSegmentService.mergeIsPossible(this.segmentItem);
            },
            splitPossible(): boolean {
                return this.translationSegmentService.splitIsPossible(this.segmentItem);
            },
            currentDocumentType(): number {
                return this.documentStore.documentType;
            },
            isIdmlDocument(): boolean {
                return this.documentStore.isIdmlDocument;
            },
        },
  
        watch: {
            'modelValue.focused'(newVal: boolean) {
                if (newVal) {
                    this.scrollToSegment();
                }
            }
        },
  
        methods: {
            scrollToSegment() {                
                const segmentElement = this.$refs.translationSegment as HTMLElement;
                if (!segmentElement) return;
      
                const inViewPort = this.elementIsVisibleInViewport(segmentElement);
                const viewOptions = this.getViewOptions(inViewPort);
                (this.$refs as any).translationSegment.scrollIntoView(viewOptions);
            },
    
            elementIsVisibleInViewport(el: HTMLElement) {
                const { top, left, bottom, right } = el.getBoundingClientRect();
                const { innerHeight, innerWidth } = window;
                return top >= 0 && left >= 0 && bottom <= innerHeight && right <= innerWidth;
            },
    
            getViewOptions(elementInViewPort: boolean) {
                return { 
                    behavior: elementInViewPort ? 'smooth' : 'auto',
                    block: 'center', 
                    inline: 'center'
                };
            },
    
            handleMerge() {
                this.$emit('merge-segment', this.translationSegment.translationSegmentId);
            },
    
            handleSplit() {
                this.$emit('split-segment', this.translationSegment.translationSegmentId);
            },
    
            handleNextSegment() {
                this.$emit('next-translate-segment', this.translationSegment.translationSegmentId);
            },
        },
    });
</script>

<style>
    .el-popover.el-popper.variants-popover {
        font-size: 19px;
    }

    .ql-container p {
        font-size: 1.4em;
    }
</style>

<style lang="scss" scoped>
    .segment-wrapper {

        .segment-box {
            display: flex;
            padding: 35px;

            .number {
                font-weight: 600;
                width: 18px;
                margin-top: 18px;
                display: flex;
                justify-content: flex-end;
            }

            .source-text {
                width: 445px;
                margin-right: 35px;
                display: flex;
                padding: 6px 3px;
                text-align: justify;
            }
        }

        .focused-row, .focused-row:hover {
            background-color: var(--light-blue-focused);
        }
    }

    .text-group {
        display: flex;
        flex-direction: column;
        align-items: center;

        .source-target {
            display: flex
        }
    }


    .next-segment-wrapper {
        position: relative;
    }

    .hidden {
        display: none;
    
        &.show {
            display: block;
        }
    }
</style>